import api, { getBackendRoot } from "@/lib/api";

export function wsUrl(token) {
  const backend = getBackendRoot() || (typeof window !== "undefined" ? window.location.origin : "");
  const proto = backend.startsWith("https") ? "wss" : "ws";
  return backend.replace(/^https?/, proto) + "/api/ws?token=" + encodeURIComponent(token);
}

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  const arr = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) arr[i] = raw.charCodeAt(i);
  return arr;
}

/**
 * @returns {Promise<{ ok: boolean, error?: string }>}
 */
export async function enablePush({ requestPermission = true } = {}) {
  try {
    if (!("serviceWorker" in navigator) || !("PushManager" in window)) {
      return { ok: false, error: "unsupported" };
    }
    const reg = await navigator.serviceWorker.register("/sw.js", { scope: "/", updateViaCache: "none" });
    await navigator.serviceWorker.ready;
    if (!navigator.serviceWorker.controller) {
      await new Promise((resolve) => {
        const t = setTimeout(resolve, 4000);
        navigator.serviceWorker.addEventListener(
          "controllerchange",
          () => {
            clearTimeout(t);
            resolve();
          },
          { once: true },
        );
      });
    }
    if (Notification.permission === "denied") {
      return { ok: false, error: "denied" };
    }
    if (requestPermission && Notification.permission === "default") {
      const perm = await Notification.requestPermission();
      if (perm !== "granted") {
        return { ok: false, error: perm === "denied" ? "denied" : "dismissed" };
      }
    } else if (Notification.permission !== "granted") {
      return { ok: false, error: "not_granted" };
    }
    const { data } = await api.get("/push/vapid-public-key");
    if (!data.key) {
      return { ok: false, error: "no_vapid" };
    }
    if (String(data.key).includes("object") || String(data.key).includes("<")) {
      return { ok: false, error: "push_server_misconfigured" };
    }
    const appKey = urlBase64ToUint8Array(data.key);
    let sub = await reg.pushManager.getSubscription();
    if (sub) {
      try {
        await api.post("/push/subscribe", sub.toJSON());
        return { ok: true };
      } catch {
        await sub.unsubscribe().catch(() => {});
        sub = null;
      }
    }
    try {
      sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: appKey,
      });
    } catch (e) {
      const msg = e?.message || String(e);
      if (/push service error/i.test(msg)) {
        return { ok: false, error: "push_service_error" };
      }
      return { ok: false, error: msg };
    }
    await api.post("/push/subscribe", sub.toJSON());
    return { ok: true };
  } catch (e) {
    if (e?.response?.status === 401) {
      return { ok: false, error: "Jelentkezz be újra, majd próbáld megint." };
    }
    const msg = e?.response?.data?.detail || e?.message || String(e);
    if (/push service error/i.test(msg)) {
      return { ok: false, error: "push_service_error" };
    }
    return { ok: false, error: msg };
  }
}

export function formatTime(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleTimeString("hu-HU", { hour: "2-digit", minute: "2-digit" });
}

/** Inbox sor: ma → óra:perc, korábban → hónap nap. */
export function formatConversationDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const now = new Date();
  if (d.toDateString() === now.toDateString()) return formatTime(iso);
  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  if (d.toDateString() === yesterday.toDateString()) return "Tegnap";
  return d.toLocaleDateString("hu-HU", { month: "short", day: "numeric" });
}
